import { Injectable } from '@angular/core';
import { LocalSettingsKey } from '@shared/types/local-settings';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  lastAppliedTheme: string;

  constructor() {}

  get isActiveThemeLight(): boolean {
    return this.getActiveTheme() === 'lightTheme';
  }

  get isActiveThemeDark(): boolean {
    return this.getActiveTheme() === 'darkTheme';
  }

  getActiveTheme(): string {
    return localStorage.getItem(LocalSettingsKey.THEME) || (this.isSystemSchemeDark() ? 'darkTheme' : 'lightTheme');
  }

  isSystemSchemeDark(): boolean {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

  setTheme(theme: string): void {
    localStorage.setItem(LocalSettingsKey.THEME, theme);
  }

  applyTheme(): void {
    const theme = this.getActiveTheme();

    if (theme === this.lastAppliedTheme) {
      return;
    }

    if (theme === 'darkTheme') {
      this.applyDarkTheme();
    } else {
      this.applyLightTheme();
    }
  }

  applyDarkTheme(): void {
    this.lastAppliedTheme = 'darkTheme';

    document.documentElement.style.setProperty('--primary-main-color', '#698dac');
    document.documentElement.style.setProperty('--primary-anchor-color', '#0469d0');

    document.documentElement.style.setProperty('--secondary-01-color', '#f2f2f2');
    document.documentElement.style.setProperty('--secondary-02-color', '#a6a8ab');
    document.documentElement.style.setProperty('--secondary-03-color', '#8f9294');
    document.documentElement.style.setProperty('--secondary-04-color', '#6D6F70');

    document.documentElement.style.setProperty('--primary-main-bg', '#1c1e1f');
    document.documentElement.style.setProperty('--secondary-bg', '#131314');
    document.documentElement.style.setProperty('--thirdary-bg', '#424345');

    document.documentElement.style.setProperty('--input-font-color', '#f2f2f2');
    document.documentElement.style.setProperty('--info-bg-color', '#212d33');
    document.documentElement.style.setProperty('--modal-backdrop-bg', '#616263');
    document.documentElement.style.setProperty('--spinner-loading-bg', '#1C1E1F');
    document.documentElement.style.setProperty('--own-mention-bg', '#212d33');
    document.documentElement.style.setProperty('--own-mention-bg-hover', '#212d3388');

    document.documentElement.style.setProperty('--read-pane-bg-color', '#1c1e1f');
    document.documentElement.style.setProperty('--read-pane-font-color', '#f2f2f2');

    document.documentElement.style.setProperty('--borders-hard', '#8f9294');
    document.documentElement.style.setProperty('--borders-medium', '#131314');
    document.documentElement.style.setProperty('--borders-light', '#424345');
    document.documentElement.style.setProperty('--default-icon', '#8f9294');
    document.documentElement.style.setProperty('--default-shadow', '#f2f2f229');

    // Buttons
    document.documentElement.style.setProperty('--primary-btn-bg', '#698dac');
    document.documentElement.style.setProperty('--primary-btn-font-color', '#f2f2f2');
    document.documentElement.style.setProperty('--primary-btn-hover-bg', '#7fa9ce');
    document.documentElement.style.setProperty('--primary-btn-pressed-bg', '#5e7d98');
    document.documentElement.style.setProperty('--primary-btn-disabled-bg', '#333f49');
    document.documentElement.style.setProperty('--primary-btn-disabled-font-color', '#1c1e1f');
    document.documentElement.style.setProperty('--primary-btn-destructive-bg', '#f05f6d');
    document.documentElement.style.setProperty('--primary-btn-destructive-font-color', '#ffffff');
    document.documentElement.style.setProperty('--primary-btn-destructive-hover-bg', '#ff6e7c');
    document.documentElement.style.setProperty('--primary-btn-destructive-pressed-bg', '#c75d68');
    document.documentElement.style.setProperty('--primary-btn-destructive-disabled-bg', '#f9bfc4');
    document.documentElement.style.setProperty('--secondary-btn-bg', '#131314');
    document.documentElement.style.setProperty('--secondary-btn-font-color', '#698dac');
    document.documentElement.style.setProperty('--secondary-btn-border-color', '#698dac');
    document.documentElement.style.setProperty('--secondary-btn-hover-bg', '#283039');
    document.documentElement.style.setProperty('--secondary-btn-hover-font-border-color', '#7fa9ce');
    document.documentElement.style.setProperty('--secondary-btn-pressed-bg', '#1a1d21');
    document.documentElement.style.setProperty('--secondary-btn-pressed-font-border-color', '#5e7d98');
    document.documentElement.style.setProperty('--secondary-btn-disabled-font-border-color', '#333f49');
    document.documentElement.style.setProperty('--secondary-btn-destructive-font-border-color', '#f05f6d');
    document.documentElement.style.setProperty('--secondary-btn-destructive-hover-bg', '#3f2226');
    document.documentElement.style.setProperty('--secondary-btn-destructive-pressed-bg', '#2a1b1d');
    document.documentElement.style.setProperty('--secondary-btn-destructive-disabled-bg', '#131314');
    document.documentElement.style.setProperty('--secondary-btn-destructive-disabled-font-border-color', '#3f2226');

    document.documentElement.style.setProperty('--tertiary-btn-bg', '#698dac');
    document.documentElement.style.setProperty('--tertiary-btn-hover-bg', '#7fa9ce');
    document.documentElement.style.setProperty('--tertiary-btn-pressed-bg', '#5e7d98');
    document.documentElement.style.setProperty('--tertiary-btn-disabled-bg', '#B6C2CC');

    // Calendar
    document.documentElement.style.setProperty('--calendar-blue', '#52748c');
    document.documentElement.style.setProperty('--calendar-blue-title', '#e1ecfc');
  }

  applyLightTheme(): void {
    this.lastAppliedTheme = 'lightTheme';

    document.documentElement.style.setProperty('--primary-main-color', '#0a3254');
    document.documentElement.style.setProperty('--primary-anchor-color', '#0050a2');

    document.documentElement.style.setProperty('--secondary-01-color', '#020203');
    document.documentElement.style.setProperty('--secondary-02-color', '#526e86');
    document.documentElement.style.setProperty('--secondary-03-color', '#8498a9');
    document.documentElement.style.setProperty('--secondary-04-color', '#8498a9');

    document.documentElement.style.setProperty('--primary-main-bg', '#FFF');
    document.documentElement.style.setProperty('--secondary-bg', '#f3f5f7');
    document.documentElement.style.setProperty('--thirdary-bg', '#e7ebee');

    document.documentElement.style.setProperty('--input-font-color', '#020203');
    document.documentElement.style.setProperty('--info-bg-color', '#e6f4fc');
    document.documentElement.style.setProperty('--modal-backdrop-bg', '#020203');
    document.documentElement.style.setProperty('--spinner-loading-bg', '#FBFBFB');
    document.documentElement.style.setProperty('--own-mention-bg', '#cddced');
    document.documentElement.style.setProperty('--own-mention-bg-hover', '#cddced88');

    document.documentElement.style.setProperty('--read-pane-bg-color', '#FFF');
    document.documentElement.style.setProperty('--read-pane-font-color', '#020203');

    document.documentElement.style.setProperty('--borders-hard', '#8498a9');
    document.documentElement.style.setProperty('--borders-medium', '#f3f5f7');
    document.documentElement.style.setProperty('--borders-light', '#e7ebee');
    document.documentElement.style.setProperty('--default-icon', '#8498a9');
    document.documentElement.style.setProperty('--default-shadow', '#00000029');

    // Buttons
    document.documentElement.style.setProperty('--primary-btn-bg', '#0a3254');
    document.documentElement.style.setProperty('--primary-btn-font-color', '#FFF');
    document.documentElement.style.setProperty('--primary-btn-hover-bg', '#0d416e');
    document.documentElement.style.setProperty('--primary-btn-pressed-bg', '#062139');
    document.documentElement.style.setProperty('--primary-btn-disabled-bg', '#b6c2cc');
    document.documentElement.style.setProperty('--primary-btn-disabled-font-color', '#FFF');
    document.documentElement.style.setProperty('--primary-btn-destructive-bg', '#f05f6d');
    document.documentElement.style.setProperty('--primary-btn-destructive-font-color', '#ffffff');
    document.documentElement.style.setProperty('--primary-btn-destructive-hover-bg', '#ff6e7c');
    document.documentElement.style.setProperty('--primary-btn-destructive-pressed-bg', '#c75d68');
    document.documentElement.style.setProperty('--primary-btn-destructive-disabled-bg', '#f9bfc4');
    document.documentElement.style.setProperty('--secondary-btn-bg', '#FFF');
    document.documentElement.style.setProperty('--secondary-btn-font-color', '#0a3254');
    document.documentElement.style.setProperty('--secondary-btn-border-color', '#0a3254');
    document.documentElement.style.setProperty('--secondary-btn-hover-bg', '#f1f2f4');
    document.documentElement.style.setProperty('--secondary-btn-hover-font-border-color', '#0d416e');
    document.documentElement.style.setProperty('--secondary-btn-pressed-bg', '#e4e7ea');
    document.documentElement.style.setProperty('--secondary-btn-pressed-font-border-color', '#062139');
    document.documentElement.style.setProperty('--secondary-btn-disabled-font-border-color', '#b6c2cc');
    document.documentElement.style.setProperty('--secondary-btn-destructive-font-border-color', '#f05f6d');
    document.documentElement.style.setProperty('--secondary-btn-destructive-hover-bg', '#fff7f8');
    document.documentElement.style.setProperty('--secondary-btn-destructive-pressed-bg', '#ffebec');
    document.documentElement.style.setProperty('--secondary-btn-destructive-disabled-bg', '#d9c3c5');
    document.documentElement.style.setProperty('--secondary-btn-destructive-disabled-font-border-color', '#baa2a4');

    document.documentElement.style.setProperty('--tertiary-btn-bg', '#FFF');
    document.documentElement.style.setProperty('--tertiary-btn-font-color', '#0050a2');
    document.documentElement.style.setProperty('--tertiary-btn-hover-bg', '#e7eef6');
    document.documentElement.style.setProperty('--tertiary-btn-pressed-bg', '#cddced');
    document.documentElement.style.setProperty('--tertiary-btn-pressed-font-color', '#003164');
    document.documentElement.style.setProperty('--tertiary-btn-disabled-bg', '#FFF');
    document.documentElement.style.setProperty('--tertiary-btn-disabled-font-color', '#b6c2cc');
    document.documentElement.style.setProperty('--tertiary-btn-destructive-bg', 'inherit');
    document.documentElement.style.setProperty('--tertiary-btn-destructive-font-color', '#f05f6d');
    document.documentElement.style.setProperty('--tertiary-btn-destructive-hover-bg', '#fff7f8');
    document.documentElement.style.setProperty('--tertiary-btn-destructive-pressed-bg', '#ffebec');
    document.documentElement.style.setProperty('--tertiary-btn-destructive-disabled-bg', '#FFF');

    // Calendar
    document.documentElement.style.setProperty('--calendar-blue', '#a5c3e2');
    document.documentElement.style.setProperty('--calendar-blue-title', '#cddef7');
  }

  turnLightsOn(): void {
    document.documentElement.style.setProperty('--read-pane-bg-color', '#FFF');
    document.documentElement.style.setProperty('--read-pane-font-color', '#020203');
  }

  turnLightsOff(): void {
    document.documentElement.style.setProperty('--read-pane-bg-color', '#1c1e1f');
    document.documentElement.style.setProperty('--read-pane-font-color', '#f2f2f2');
  }
}
